import React from 'react';
import { Droplet, Flame } from 'lucide-react';

const GasolinePhilosophy = () => {
  return (
    <section id="philosophy" className="relative py-24 bg-[#0a0a0a] overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full px-6 py-2 mb-6">
            <Flame className="w-5 h-5 text-[#D4AF37]" />
            <span className="text-[#D4AF37] font-bold text-sm">THE GASOLINE PHILOSOPHY</span>
          </div>
          <h2 className="text-5xl sm:text-6xl md:text-7xl font-bold text-white mb-6" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>
            FROM
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-[#FFD700]">
              CRUDE OIL TO FUEL
            </span>
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Animated Oil Drum Visual */}
          <div className="relative">
            <div className="relative w-full max-w-md mx-auto">
              {/* Oil Drum Container */}
              <div className="relative bg-gradient-to-b from-[#2a2a2a] to-[#1a1a1a] border-4 border-[#D4AF37] rounded-lg p-8 overflow-hidden">
                {/* Animated Oil Level */}
                <div className="absolute bottom-0 left-0 right-0 h-3/4 bg-gradient-to-t from-[#1a1a1a] via-[#2a2a2a] to-transparent animate-pulse opacity-50" />
                
                {/* Oil Drum Icon */}
                <div className="relative z-10 flex flex-col items-center justify-center h-80">
                  <div className="w-40 h-40 bg-gradient-to-br from-[#D4AF37] to-[#FFD700] rounded-full flex items-center justify-center mb-6 animate-pulse">
                    <Droplet className="w-20 h-20 text-[#0a0a0a]" />
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-[#D4AF37] mb-2" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>
                      CRUDE OIL
                    </div>
                    <div className="text-lg text-gray-400 mb-4">Raw potential</div>
                    <div className="w-2 h-12 bg-gradient-to-b from-[#D4AF37] to-transparent mx-auto mb-4" />
                    <div className="text-3xl font-bold text-[#FFD700] mb-2" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>
                      GASOLINE
                    </div>
                    <div className="text-lg text-gray-400">Refined purpose</div>
                  </div>
                </div>
              </div>
              
              {/* Decorative Elements */}
              <div className="absolute -top-4 -right-4 w-32 h-32 bg-[#D4AF37]/20 rounded-full filter blur-3xl animate-pulse" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-[#FFD700]/20 rounded-full filter blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
            </div>
          </div>

          {/* Philosophy Text */}
          <div>
            <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border border-[#D4AF37]/20 rounded-2xl p-8 md:p-10">
              <h3 className="text-3xl font-bold text-white mb-6" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>
                THE TRANSFORMATION PROCESS
              </h3>
              
              <p className="text-xl text-gray-300 mb-6 leading-relaxed">
                Just as crude oil must be refined to become gasoline, a source of power and purpose, so too must we refine our raw experiences into something that fuels transformation.
              </p>

              {/* Blockquote */}
              <div className="bg-[#E63946]/10 border-l-4 border-[#E63946] rounded-r-lg p-6 mb-6">
                <blockquote className="text-2xl md:text-3xl text-white font-semibold mb-4 leading-relaxed italic">
                  "We all have crude oil buried beneath us. The question is: will you choose to dig it up and refine it, or leave it in the ground?"
                </blockquote>
                <div className="flex items-center space-x-3">
                  <div className="w-1 h-10 bg-[#D4AF37]" />
                  <div>
                    <p className="text-[#D4AF37] font-bold">The Choice</p>
                    <p className="text-gray-400 text-sm">Extract, refine, and transform</p>
                  </div>
                </div>
              </div>

              <p className="text-gray-300 leading-relaxed">
                The Gasoline curriculum guides you through this refining process. It extracts lessons from your past, processes them with intention, and converts them into fuel that powers your future and inspires others.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GasolinePhilosophy;
